import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import TriviaIcon from '../assets/icons/trivia.png';
import GameIcon from '../assets/icons/game.webp';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import Swal from 'sweetalert2';

const EarnFeatures: React.FC = () => {
  const [userStatus, setUserStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true); // Loading state
  const navigate = useNavigate();
  const db = getFirestore();

  // Fetch the user's status from Firestore
  useEffect(() => {
    const fetchUserStatus = async () => {
      try {
        const auth = getAuth();
        const userId = auth.currentUser?.uid; // Get the logged-in user's ID from Firebase Auth

        if (!userId) {
          console.error('No user is logged in');
          setUserStatus(null);
          setLoading(false); // Set loading to false after failing to get the user ID
          return;
        }

        // Reference to the user's document in Firestore
        const userDocRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserStatus(userData?.status); // Set user status to the Firestore status field
        } else {
          console.error('User document not found');
          setUserStatus(null);
        }
      } catch (error) {
        console.error('Error fetching user status:', error);
        setUserStatus(null);
      } finally {
        setLoading(false); // Always set loading to false once data fetching is done
      }
    };

    fetchUserStatus();
  }, [db]);

  // Handle trivia click
  const handleTriviaClick = () => {
    // Check if status is verified
    if (userStatus === 'verified') {
      navigate('/quizscreen'); // Navigate to the quiz screen if the user's status is 'verified'
    } else if (userStatus === 'unverified') {
      Swal.fire({
        icon: 'warning',
        title: 'Account Not Verified',
        text: 'Please verify your account to play and earn.',
        confirmButtonText: 'Okay',
      });
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Unable to determine account status. Please try again later.',
        confirmButtonText: 'Okay',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center my-4">
        <div className="spinner-border animate-spin text-gray-600" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 my-4 h-screen overflow-y-auto p-4">
      <div
        onClick={handleTriviaClick}
        className="p-3 bg-gray-800 rounded-lg shadow-md flex justify-between items-center cursor-pointer transition-all duration-200 hover:bg-gray-700"
      >
        <img src={TriviaIcon} alt="Trivia Icon" className="w-8 h-8 rounded-full" />
        <p className="font-medium text-gray-100 text-sm">Answer Trivia Questions</p>
        <ArrowRight size={16} className="text-blue-400" />
      </div>
      <a
        href="https://www.profitablecpmrate.com/s0gfx7d1?key=0910fe7dbe9de20f8f11e4a5005c6630"
        className="block"
      >
        <div className="p-3 bg-gray-800 rounded-lg shadow-md flex justify-between items-center cursor-pointer transition-all duration-200 hover:bg-gray-700">
          <img src={GameIcon} alt="Game Icon" className="w-8 h-8 rounded-full" />
          <p className="font-medium text-gray-100 text-sm">Play to Earn Cash</p>
          <ArrowRight size={16} className="text-blue-400" />
        </div>
      </a>
    </div>
  );
};

export default EarnFeatures;
